import React, { useEffect, useMemo, useRef, useState } from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';

// OpenStreetMap Nominatim autocomplete component
export default function AddressAutocomplete({ label = 'Address', value, onSelect, onManualChange, disabled = false, required = false }) {
  const [options, setOptions] = useState([]); // [{ label, lat, lon }]
  const [inputValue, setInputValue] = useState('');
  const controllerRef = useRef(null);

  // Sync displayed text with external value
  useEffect(() => {
    setInputValue(value || '');
  }, [value]);


  // Debounced fetch to Nominatim
  useEffect(() => {
    const query = inputValue?.trim();
    if (!query || query.length < 3) {
      setOptions([]);
      return;
    }

    const handler = setTimeout(async () => {
      try {
        // Cancel previous request
        controllerRef.current?.abort();
        controllerRef.current = new AbortController();
        const url = new URL('https://nominatim.openstreetmap.org/search');
        url.searchParams.set('q', query);
        url.searchParams.set('format', 'json');
        url.searchParams.set('addressdetails', '1');
        url.searchParams.set('limit', '8');
        url.searchParams.set('countrycodes', 'us');

        const resp = await fetch(url.toString(), {
          method: 'GET',
          headers: { 'Accept': 'application/json' },
          signal: controllerRef.current.signal,
        });
        if (!resp.ok) throw new Error('Search failed');
        const data = await resp.json();
        const opts = (data || []).map((item) => ({
          label: item.display_name,
          lat: parseFloat(item.lat),
          lon: parseFloat(item.lon),
        })).filter((o) => o.label && !Number.isNaN(o.lat) && !Number.isNaN(o.lon));
        setOptions(opts);
      } catch (_) {
        // Ignore abort/errors; keep options as-is
      }
    }, 300);

    return () => clearTimeout(handler);
  }, [inputValue]);

  const filterOptions = useMemo(() => (x) => x, []); // Don't filter client-side; we already queried

  return (
    <Autocomplete
      freeSolo
      disableClearable
      options={options}
      filterOptions={filterOptions}
      getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt.label)}
      inputValue={inputValue}
      onInputChange={(_, newInput) => {
        setInputValue(newInput);
        onManualChange && onManualChange(newInput);
      }}
      onChange={(_, newValue) => {
        if (newValue && typeof newValue === 'object') {
          // Provide both human-readable address and location coordinates
          onSelect && onSelect({ address: newValue.label, location: JSON.stringify([newValue.lat, newValue.lon]) });
          setInputValue(newValue.label);
        } else if (typeof newValue === 'string') {
          onSelect && onSelect({ address: newValue, location: '' });
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          margin="normal"
          required={required}
          disabled={disabled}
          placeholder="Type address to search"
        />
      )}
    />
  );
}


